import React from 'react';
import {Box, Typography, Container, Grid, alpha, useTheme, Stack, Button} from '@mui/material';
import {useTranslation} from "react-i18next";
import {useAuth0} from "@auth0/auth0-react";
import {Link} from "react-router-dom";
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsIcon from '@mui/icons-material/Sms';
import BarChartIcon from '@mui/icons-material/BarChart';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PaletteIcon from '@mui/icons-material/Palette';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

const FeaturesSection = () => {
  const {t} = useTranslation();
  const {loginWithRedirect} = useAuth0();
  const theme = useTheme();

  const features = [
    {
      icon: DashboardCustomizeIcon,
      title: t('Full admin panel'),
      description: t('Manage everything from one place: menu items, orders, customers, and settings. Intuitive interface built for speed.'),
    },
    {
      icon: BarChartIcon,
      title: t('Real-time analytics'),
      description: t('Track orders, revenue, popular items, and customer behavior. Make data-driven decisions to grow your business.'),
    },
    {
      icon: MailOutlineIcon,
      title: t('Email receipts'),
      description: t('Customers get instant email confirmations. Build trust and keep records automatically.'),
    },
    {
      icon: SmsIcon,
      title: t('SMS notifications'),
      description: t('Alert customers when their order is ready. Keep them informed with automated SMS updates.'),
    },
    {
      icon: LoyaltyIcon,
      title: t('Loyalty system'),
      description: t('Reward repeat customers. Built-in loyalty program to increase retention and lifetime value.'),
    },
    {
      icon: PictureAsPdfIcon,
      title: t('PDF menu download'),
      description: t('Generate beautiful PDF menus for print. Always have a backup option for customers who prefer paper.'),
    },
    {
      icon: PaletteIcon,
      title: t('Custom designs'),
      description: t('Match your brand. Customize colors, logos, and layout to reflect your business identity.'),
    },
    {
      icon: CloudDownloadIcon,
      title: t('Cloud-based'),
      description: t('Access from anywhere. Your data is backed up and secure. No installation needed.'),
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        py: {xs: 8, md: 12},
        bgcolor: 'background.paper',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
            }}
          >
            {t("FEATURES")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Everything you need to succeed")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {t("Powerful features designed to help small businesses compete with the big players")}
          </Typography>
        </Box>

        {/* Main Feature Showcase */}
        <Box
          sx={{
            mb: 8,
            p: {xs: 4, md: 6},
            borderRadius: '20px',
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: 'text.primary',
                  fontSize: {xs: '1.75rem', md: '2.5rem'},
                }}
              >
                {t("Beautiful digital menus that convert")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                {t("Create stunning, mobile-optimized menus in minutes. Add photos, descriptions, prices, and variants. Your customers will love the experience.")}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 2 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  ✓ {t("Mobile-first design")}
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  ✓ {t("Multi-language support")}
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  ✓ {t("Instant updates")}
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Screenshot placeholder */}
              <Box
                component="img"
                src="/images/startPage/demo.png"
                alt={t("Beautiful digital menus that convert")}
                sx={{
                  width: '100%',
                  maxWidth: {xs: '100%', sm: '400px', md: '500px'},
                  height: 'auto',
                  borderRadius: '12px',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  mx: 'auto',
                  display: 'block',
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* CTA Button */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Link
            to="/"
            onClick={() => loginWithRedirect({screen_hint: 'signup'})}
            style={{textDecoration: 'none'}}
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              {t("Start Free Trial")}
            </Button>
          </Link>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: '12px',
                    bgcolor: 'background.default',
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 32,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: 'text.primary',
                      fontSize: '1rem',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      fontSize: '0.875rem',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
